import { z } from "zod";
import { AiProposalArtifactV1Schema } from "./ai-proposal";
import {
  ActionTypeSchema,
  AiJobTypeSchema,
  AiLogStatusSchema,
  AiRunStatusSchema,
  AiTriggerTypeSchema,
  ConfidenceSchema,
  ExecutionModeLogSchema,
  RiskLevelSchema,
} from "./approval";

export const AiRunDetailAvailabilitySchema = z.enum(["available", "legacy_list_only"]);
export type AiRunDetailAvailability = z.infer<typeof AiRunDetailAvailabilitySchema>;

export const aiOperationsRunSchema = z.object({
  id: z.string().uuid(),
  // ADP-110: disambiguates runs across shops in the aggregated history.
  shopId: z.string().default(""),
  shopName: z.string().nullable().default(null),
  /** Immutable group identity captured by this Run; membership may have changed since. */
  managedGroupId: z.string().uuid().nullable().default(null),
  managedGroupName: z.string().nullable().default(null),
  campaignId: z.string().uuid().nullable(),
  campaignName: z.string().nullable(),
  jobType: AiJobTypeSchema,
  triggerType: AiTriggerTypeSchema,
  /** ADP-270: demo deployments only — standard demo model or live LLM. */
  modelMode: z.enum(["demo_standard", "live"]).nullable().default(null),
  batchId: z.string().uuid().nullable(),
  triggeredByName: z.string().nullable(),
  status: AiRunStatusSchema,
  detailAvailability: AiRunDetailAvailabilitySchema,
  highestRisk: RiskLevelSchema.nullable(),
  pendingDecisions: z.number().int().nonnegative(),
  candidateActions: z.number().int().nonnegative(),
  autoExecuted: z.number().int().nonnegative(),
  approvalCreated: z.number().int().nonnegative(),
  /** 全路由(auto+approval,含回滚)的已执行动作数;autoExecuted 只数 auto 路由。 */
  executedActions: z.number().int().nonnegative().default(0),
  /** 被人工拒绝的动作数,用于列表把"审批后拒绝"和"仅建议"区分开。 */
  rejectedActions: z.number().int().nonnegative().default(0),
  /** 已执行完成的回滚补偿动作数;>0 表示该运行的变更被人工回滚过。 */
  rolledBackActions: z.number().int().nonnegative().default(0),
  skipped: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  failureReason: z.string().nullable(),
  startedAt: z.string().datetime({ offset: true }),
  finishedAt: z.string().datetime({ offset: true }).nullable(),
  /** ADP-127: 单竞价动作运行的变化,直接在列表行展示;其余为 null。 */
  singleBidChange: z.object({ oldBid: z.number(), newBid: z.number() }).nullable().default(null),
});
export type AiOperationsRun = z.infer<typeof aiOperationsRunSchema>;

/**
 * User-facing Run status is a projection of two orthogonal dimensions:
 * the business outcome and an optional technical incident. Keeping both means
 * a saved rejection can still surface a later finalization failure accurately.
 */
export const AiRunPrimaryOutcomeSchema = z.enum([
  "needsDecision",
  "analyzing",
  "finalizing",
  "approvedPendingExecution",
  "approved",
  "noChanges",
  "executing",
  "executed",
  "rolledBack",
  "rejected",
  "expired",
  "recommended",
  "partialRecommended",
  "partial",
  "completed",
  "observed",
]);
export type AiRunPrimaryOutcome = z.infer<typeof AiRunPrimaryOutcomeSchema>;

export const AiRunIncidentSchema = z.enum([
  "executionFailed",
  "finalizationFailed",
  "rollbackFailed",
  "unknownFailure",
]);
export type AiRunIncident = z.infer<typeof AiRunIncidentSchema>;

export const AiRunDisplayStatusSchema = z
  .object({
    outcome: AiRunPrimaryOutcomeSchema.nullable(),
    incident: AiRunIncidentSchema.nullable(),
  })
  .strict()
  .refine(({ outcome, incident }) => outcome !== null || incident !== null, {
    message: "A display status requires an outcome or incident",
  });
export type AiRunDisplayStatus = z.infer<typeof AiRunDisplayStatusSchema>;

export const aiOperationsRunsResponseSchema = z.object({
  hasMore: z.boolean().default(false),
  runs: z.array(aiOperationsRunSchema),
  // ADP-110: scoped shops left out of the aggregate (disconnected) — surfaced,
  // never silent.
  excludedShops: z.number().int().nonnegative().default(0),
});
export type AiOperationsRunsResponse = z.infer<typeof aiOperationsRunsResponseSchema>;

export const aiOperationsRunActionSchema = z.object({
  id: z.string().uuid(),
  proposalId: z.string().uuid().nullable(),
  campaignId: z.string().uuid(),
  actionType: ActionTypeSchema,
  searchTermText: z.string().nullable(),
  oldBid: z.number().finite().nullable(),
  newBid: z.number().finite().nullable(),
  changePercentage: z.number().finite().nullable(),
  riskLevel: RiskLevelSchema,
  confidence: ConfidenceSchema,
  requiresApproval: ExecutionModeLogSchema,
  status: AiLogStatusSchema,
  /** 人工回滚产生的补偿动作指向被回滚的原动作;普通动作为 null。 */
  rollbackOfActionId: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type AiOperationsRunAction = z.infer<typeof aiOperationsRunActionSchema>;

export const aiOperationsProposalRoundSchema = z.object({
  id: z.string().uuid(),
  roundNumber: z.number().int().positive(),
  actionCount: z.number().int().nonnegative(),
  conclusionCount: z.number().int().nonnegative(),
  closedAt: z.string().datetime({ offset: true }).nullable(),
  closedReason: z.literal("revision_requested").nullable(),
  supersededById: z.string().uuid().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});
export type AiOperationsProposalRound = z.infer<typeof aiOperationsProposalRoundSchema>;

export const aiOperationsCurrentProposalSchema = aiOperationsProposalRoundSchema.extend({
  artifact: AiProposalArtifactV1Schema,
});
export type AiOperationsCurrentProposal = z.infer<typeof aiOperationsCurrentProposalSchema>;

export const aiOperationsRunDetailResponseSchema = z.object({
  run: aiOperationsRunSchema,
  actions: z.array(aiOperationsRunActionSchema),
  currentProposal: aiOperationsCurrentProposalSchema.nullable(),
  proposalRounds: z.array(aiOperationsProposalRoundSchema),
});
export type AiOperationsRunDetailResponse = z.infer<typeof aiOperationsRunDetailResponseSchema>;

export const triggerAiRunResponseSchema = z.object({
  status: z.literal("queued"),
  campaignId: z.string().uuid(),
  triggerType: z.literal("manual"),
});
export type TriggerAiRunResponse = z.infer<typeof triggerAiRunResponseSchema>;

export const triggerAiRunsBulkResponseSchema = z.object({
  queued: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  batchId: z.string().uuid(),
});
export type TriggerAiRunsBulkResponse = z.infer<typeof triggerAiRunsBulkResponseSchema>;
